import React, { useState, useEffect } from 'react';
import { apiClient } from '../../utils/api';
import { AdminUser } from '../../types/admin';
import { PaginationControls } from '../../components/admin/PaginationControls';
import {
  Search,
  RefreshCw,
  UserPlus,
  Eye,
  Trash2,
  CheckCircle,
  XCircle,
  Key,
} from 'lucide-react';

export const UsersTab: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const limit = 20;

  useEffect(() => {
    fetchUsers();
  }, [page, searchTerm, roleFilter, statusFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      if (searchTerm) params.append('search', searchTerm);
      if (roleFilter !== 'all') params.append('role', roleFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);

      const response = await apiClient.get(`/api/admin/users?${params.toString()}`);
      setUsers(response.data.users);
      setTotalPages(response.data.pagination.totalPages);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    alert('创建用户功能待实现');
  };

  const handleViewUser = (user: AdminUser) => {
    alert(`查看用户: ${user.email}\n用户ID: ${user.id}`);
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('确定要删除此用户吗？此操作不可撤销。')) {
      return;
    }
    try {
      await apiClient.delete(`/api/admin/users/${userId}`);
      fetchUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert('删除用户失败');
    }
  };

  const handleToggleStatus = async (userId: string, currentStatus: boolean) => {
    try {
      await apiClient.put(`/api/admin/users/${userId}/status`, {
        is_active: !currentStatus,
      });
      fetchUsers();
    } catch (error) {
      console.error('Failed to toggle user status:', error);
      alert('更新用户状态失败');
    }
  };

  const handleUpdateRole = async (userId: string, newRole: string) => {
    try {
      await apiClient.put(`/api/admin/users/${userId}/role`, { role: newRole });
      fetchUsers();
    } catch (error) {
      console.error('Failed to update user role:', error);
      alert('更新用户角色失败');
    }
  };

  const handleResetPassword = async (userId: string) => {
    if (!confirm('确定要重置此用户的密码吗？')) {
      return;
    }
    try {
      const response = await apiClient.post(`/api/admin/users/${userId}/reset-password`);
      alert(`密码已重置。新密码: ${response.data.data.new_password}`);
    } catch (error) {
      console.error('Failed to reset password:', error);
      alert('重置密码失败');
    }
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">用户管理</h2>
        <button
          onClick={handleCreateUser}
          className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white rounded-md"
        >
          <UserPlus className="h-4 w-4" />
          <span>创建用户</span>
        </button>
      </div>

      {/* 过滤器 */}
      <div className="bg-[#161b22] rounded-xl border border-cyan-900/30 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="搜索用户..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                className="pl-10 pr-4 py-2 w-full border border-cyan-900/30 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-[#0d1117] text-white"
              />
            </div>
          </div>
          <div className="flex gap-4">
            <select
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2 border border-cyan-900/30 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-[#0d1117] text-white"
            >
              <option value="all">所有角色</option>
              <option value="user">用户</option>
              <option value="moderator">版主</option>
              <option value="admin">管理员</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2 border border-cyan-900/30 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-[#0d1117] text-white"
            >
              <option value="all">所有状态</option>
              <option value="active">活跃</option>
              <option value="inactive">停用</option>
            </select>
          </div>
        </div>
      </div>

      {/* 用户表格 */}
      <div className="bg-[#161b22] rounded-xl border border-cyan-900/30 overflow-hidden">
        <table className="min-w-full divide-y divide-cyan-900/30">
          <thead className="bg-[#0d1117]">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                用户
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                角色
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                状态
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                配额
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                创建时间
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                操作
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-cyan-900/30">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-cyan-900/10">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div>
                      <div className="text-sm font-medium text-white">{user.email}</div>
                      {user.username && (
                        <div className="text-sm text-gray-400">@{user.username}</div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <select
                    value={user.role}
                    onChange={(e) => handleUpdateRole(user.id, e.target.value)}
                    className="text-sm border border-cyan-900/30 rounded px-2 py-1 bg-[#0d1117] text-white"
                  >
                    <option value="user">用户</option>
                    <option value="moderator">版主</option>
                    <option value="admin">管理员</option>
                  </select>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => handleToggleStatus(user.id, user.is_active)}
                    className="flex items-center space-x-1"
                  >
                    {user.is_active ? (
                      <>
                        <CheckCircle className="h-4 w-4 text-green-400" />
                        <span className="text-sm text-green-400">活跃</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="h-4 w-4 text-red-400" />
                        <span className="text-sm text-red-400">停用</span>
                      </>
                    )}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                  {user.quota_used} / {user.quota_limit}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                  {new Date(user.created_at).toLocaleDateString('zh-CN')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                  <button
                    onClick={() => handleViewUser(user)}
                    className="text-cyan-400 hover:text-cyan-300"
                    title="查看详情"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleResetPassword(user.id)}
                    className="text-yellow-400 hover:text-yellow-300"
                    title="重置密码"
                  >
                    <Key className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteUser(user.id)}
                    className="text-red-400 hover:text-red-300"
                    title="删除用户"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PaginationControls
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        hasNext={page < totalPages}
        hasPrev={page > 1}
      />
    </div>
  );
};
